// M4 — "🏠 For sale / for rent" block inside the DetailsPanel.
//
// Reads property_listings.json (built by pipeline/property/...), filters
// to listings whose locality_istat matches the open comune, splits by
// deal type, and shows the top 5 of each with price + area + a link out
// to the source. Renders nothing when the comune has no listings — this
// keeps the panel uncluttered for the 8000 - N comuni without coverage.
//
// Today the dataset is a 10-row stub from italy_immobiliare_stub; the
// real multi-source pipeline (M4 weeks 1-5) replaces the file without
// changing this component.

import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { usePropertyListings, type Listing } from './data';

interface Props {
  istat: string;
}

export function PropertyListingsSection({ istat }: Props) {
  const { t } = useTranslation();
  const { data } = usePropertyListings();

  const { sale, rent, total } = useMemo(() => {
    const local = (data?.listings ?? []).filter((l) => l.locality_istat === istat);
    return {
      sale: local.filter((l) => l.deal === 'sale').slice(0, 5),
      rent: local.filter((l) => l.deal === 'rent').slice(0, 5),
      total: local.length,
    };
  }, [data, istat]);

  if (total === 0) return null;

  return (
    <details
      open
      className="group border-b border-slate-100 last:border-b-0"
    >
      <summary className="-mx-2 flex cursor-pointer list-none items-center gap-1.5 rounded px-2 py-2 text-sm font-semibold text-slate-700 transition-colors hover:bg-slate-50 hover:text-slate-900">
        <span aria-hidden="true" className="shrink-0">🏠</span>
        <span className="flex-1 truncate">
          {t('property.sectionTitle', {
            defaultValue: 'For sale / for rent',
          })}
        </span>
        <span className="shrink-0 font-mono text-[10px] text-slate-400">
          {total} {t('property.count', { defaultValue: 'listings' })}
        </span>
      </summary>
      <div className="space-y-3 pb-3">
        {sale.length > 0 && <DealList label={t('property.sale', { defaultValue: 'For sale' })} items={sale} />}
        {rent.length > 0 && (
          <DealList label={t('property.rent', { defaultValue: 'For rent' })} items={rent} isRent />
        )}
        <p className="font-mono text-[10px] text-slate-400">
          {data?._meta.note ?? t('property.sourceNote', {
            defaultValue: 'Listings aggregated from open OSM data — verify on the source before contacting.',
          })}
        </p>
      </div>
    </details>
  );
}

function DealList({ label, items, isRent = false }: { label: string; items: Listing[]; isRent?: boolean }) {
  return (
    <div>
      <p className="mb-1 font-mono text-[10px] uppercase tracking-wider text-slate-400">{label}</p>
      <ul className="space-y-1">
        {items.map((l) => (
          <li key={l.external_id} className="flex items-center justify-between gap-3 rounded border border-slate-100 bg-white px-2 py-1.5 text-xs">
            <div className="min-w-0 flex-1">
              <a
                href={l.url}
                target="_blank"
                rel="noopener noreferrer nofollow sponsored"
                className="block truncate font-medium text-slate-800 hover:text-blue-700 hover:underline"
              >
                {l.title ?? `${l.rooms ?? '?'}-room property`}
              </a>
              <span className="text-[10px] text-slate-500">
                {l.area_m2 ? `${l.area_m2} m² · ` : ''}{l.source}
              </span>
            </div>
            <span className="shrink-0 text-right">
              <span className="block font-mono text-xs font-semibold tabular-nums text-slate-900">
                €{l.price_eur.toLocaleString()}{isRent ? '/mo' : ''}
              </span>
              {l.area_m2 && (
                <span className="block font-mono text-[10px] text-slate-400">
                  €{Math.round(l.price_eur / l.area_m2)}/m²{isRent ? '/mo' : ''}
                </span>
              )}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}
